<script lang="ts">
	import '../app.css';
	import { QueryClient, QueryClientProvider } from '@tanstack/svelte-query';
	import type { Snippet } from 'svelte';
	import { onMount } from 'svelte';
	import SplashScreen from '$lib/components/app/common/SplashScreen/SplashScreen.svelte';
	import { Button, Dialog, Spinner } from '$lib/components/ui';
	import { AsyncStatus } from '$lib/enums';
	import { useAsyncCallbackValue } from '$lib/hooks/use-async-callback.svelte';
	import { ServiceWorkerService } from '$lib/services/service-worker.service';
	import { StorageService } from '$lib/services/storage.service';
	import { compositionStore } from '$lib/stores/composition.svelte';
	import { configStore } from '$lib/stores/config.svelte';

	interface Props {
		children: Snippet;
	}

	let { children }: Props = $props();

	const idbSupport = useAsyncCallbackValue(() => StorageService.checkIndexedDbSupport());

	onMount(() => {
		ServiceWorkerService.register();
		return compositionStore.track();
	});

	const queryClient = new QueryClient();

	const configError = $derived(
		configStore.error instanceof Error ? configStore.error.message : undefined
	);
</script>

{#if idbSupport.state.status === AsyncStatus.SUCCESS && idbSupport.state.data === false}
	<SplashScreen>
		<div class="box grow items-center justify-center">
			<Dialog>
				<div class="box gap-s400 p-s400 flex-col">
					<p class="text-t400">Missing Browser Feature</p>
					<p class="text-t300 opacity-p400">
						No IndexedDB support found. This application requires IndexedDB to store session data
						locally. Please make sure your browser support IndexedDB and have it enabled.
					</p>
					<p class="text-t200">
						<a
							href="https://developer.mozilla.org/en-US/docs/Web/API/IndexedDB_API"
							rel="noreferrer noopener"
							target="_blank"
						>
							What is IndexedDB?
						</a>
					</p>
				</div>
			</Dialog>
		</div>
	</SplashScreen>
{:else if configStore.status === AsyncStatus.IDLE || configStore.status === AsyncStatus.LOADING}
	<SplashScreen>
		<div class="box gap-s400 grow flex-col items-center justify-center">
			<Spinner variant="Secondary" size="600" />
			<p class="text-t400">Heating up</p>
		</div>
	</SplashScreen>
{:else if configStore.status === AsyncStatus.ERROR && !configStore.ignored}
	<SplashScreen>
		<div class="box gap-s400 grow flex-col items-center justify-center">
			<Dialog>
				<div class="box gap-s400 p-s400 flex-col">
					<div class="box gap-s100 flex-col">
						<p class="text-t400">Failed to load client configuration file.</p>
						{#if configError}
							<p class="text-t300 text-critical-main">{configError}</p>
						{/if}
					</div>
					<Button variant="Critical" onclick={() => configStore.load()}>
						<span class="text-b400">Retry</span>
					</Button>
					<Button variant="Critical" fill="Soft" onclick={() => configStore.ignore()}>
						<span class="text-b400">Continue</span>
					</Button>
				</div>
			</Dialog>
		</div>
	</SplashScreen>
{:else}
	<QueryClientProvider client={queryClient}>
		{@render children()}
	</QueryClientProvider>
{/if}
